import React, { useEffect, useState } from "react";
import Box from "@amzn/meridian/box";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Heading from "@amzn/meridian/heading";
import Text from "@amzn/meridian/text";
import Tag from "@amzn/meridian/tag";
import Loader from "@amzn/meridian/loader";
import Alert from "@amzn/meridian/alert";
import Table, { TableRow, TableCell } from "@amzn/meridian/table";
import { OutputAdjustmentEntry } from "../../../checkout/model";
import { getPlanOverrides } from "../../../../services/planOverridesService";

/**
 * Read-only Checkout overrides for a plan (UTR-REFLEX-1163).
 *
 * The Checkout page's per-process-path adjustments are persisted with the plan, so
 * they are the record of what the plan was published with. Redux only holds the
 * adjustments of a plan being built, so this view reads them back via
 * GetPlanOverrides — which is what makes them visible for a published plan or a
 * past shift's closed plan, not just during plan review.
 */
interface CheckoutOverridesViewProps {
  /** Plan whose persisted checkout overrides to show. */
  planId?: string;
}

// 100 means "no change"; the table shows the signed delta operators actually set.
const formatDelta = (overridePercentage?: number): string => {
  if (overridePercentage === undefined || overridePercentage === null) {
    return "—";
  }
  const delta = overridePercentage - 100;
  if (delta === 0) {
    return "No change";
  }
  return `${delta > 0 ? "+" : ""}${delta}%`;
};

const CheckoutOverridesView: React.FC<CheckoutOverridesViewProps> = ({ planId }) => {
  const [adjustments, setAdjustments] = useState<OutputAdjustmentEntry[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasFailed, setHasFailed] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    if (!planId) {
      setAdjustments(null);
      setHasFailed(false);
      // Also clear the loading flag: losing the planId while a fetch was in
      // flight would otherwise leave the tab stuck on the spinner for good,
      // since nothing resolves once the effect has been superseded.
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setHasFailed(false);
    getPlanOverrides(planId)
      .then((response) => {
        if (!cancelled) {
          setAdjustments(response?.outputAdjustments ?? []);
        }
      })
      .catch(() => {
        // GetPlanOverrides is the only source of these adjustments, so a failed
        // fetch must not be reported as "the plan had no adjustments" — that would
        // tell the operator something untrue about a published plan.
        if (!cancelled) {
          setAdjustments(null);
          setHasFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [planId]);

  if (isLoading) {
    return (
      <Box type="outline" spacingInset="500">
        <Column alignmentHorizontal="center">
          <Loader />
        </Column>
      </Box>
    );
  }

  if (hasFailed) {
    return (
      <Box type="outline" spacingInset="400">
        <Alert type="error" size="medium">
          Couldn&apos;t load this plan&apos;s checkout overrides. Reopen the plan to try
          again — the plan itself is unchanged.
        </Alert>
      </Box>
    );
  }

  if (!adjustments || adjustments.length === 0) {
    return (
      <Box type="outline" spacingInset="600">
        <Column spacing="200" alignmentHorizontal="center">
          <Heading type="h400" level={2}>No checkout overrides</Heading>
          <Text type="b300" color="secondary" alignment="center">
            This plan was published without any process path adjustments on the Checkout screen.
          </Text>
        </Column>
      </Box>
    );
  }

  return (
    <Box type="outline" spacingInset="400">
      <Column spacing="300">
        <Row alignmentVertical="center" spacing="300">
          <Heading type="h500" level={2}>Checkout Overrides</Heading>
          <Tag type="theme">{`${adjustments.length} path${adjustments.length === 1 ? "" : "s"}`}</Tag>
        </Row>
        <Table headerRows={1} showDividers={true} spacing="small">
          <TableRow>
            <TableCell>Process Path</TableCell>
            <TableCell>Adjustment</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Reason</TableCell>
          </TableRow>
          {adjustments.map((adjustment, index) => (
            <TableRow key={adjustment.functionId ?? `${adjustment.functionName}-${index}`}>
              <TableCell>
                <Text type="b300">{adjustment.functionName}</Text>
              </TableCell>
              <TableCell>
                {/* A disabled path contributes nothing, so its percentage is
                    meaningless — show the exclusion instead of a misleading delta. */}
                <Text type="b300">{adjustment.disabled ? "—" : formatDelta(adjustment.overridePercentage)}</Text>
              </TableCell>
              <TableCell>
                <Row>
                  <Tag type={adjustment.disabled ? "error" : "success"}>
                    {adjustment.disabled ? "Excluded" : "Included"}
                  </Tag>
                </Row>
              </TableCell>
              <TableCell>
                <Text type="b300" color="secondary">{adjustment.overrideReason || "—"}</Text>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      </Column>
    </Box>
  );
};

export default CheckoutOverridesView;
